import { SALVAR_USUARIO } from '@/modules/usuarios-edicao/data/salvar-usuario'
import { CARGOS_BY_USUARIO_QUERY, CARGOS_QUERY } from '../data/cargos'
import { INFO_USUARIO_QUERY } from '../data/info-usuario'
import { USUARIO_DOMINIO_QUERY } from '@/modules/usuarios-edicao/data/usuario-dominio-query'
import { USUARIO_QUERY } from '@/modules/usuarios-core/data/usuario-query'
import { USUARIOS_PORTAL_PROPERTIES_QUERY } from '@/modules/usuarios-edicao/data/usuarios-portal-properties'
import {
  CargosByUsuarioQuery,
  CargosByUsuarioQueryVariables,
  CargosQuery,
  CargosQueryVariables,
  InfoUsuarioQueryQuery,
  InfoUsuarioQueryQueryVariables,
  SalvarUsuarioMutation,
  SalvarUsuarioMutationVariables,
  UsuarioDominioQueryQuery,
  UsuarioDominioQueryQueryVariables,
  UsuarioQueryQuery,
  UsuarioQueryQueryVariables,
  UsuariosPortalPropertiesQueryQuery,
  UsuariosPortalPropertiesQueryQueryVariables
} from '@/core/types/graphql'
import { useConfirmModal } from '@/core/context/confirm-modal-context'
import { t } from '@lingui/core/macro'
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from '../../../core/utils/request'

export const useUsuarioEdicaoQueryHandler = () => {
  const { askUser } = useConfirmModal()
  const { id } = useParams()
  const client = useQueryClient()
  const { data: usuarioData, isLoading: usuarioLoading } = useQuery<
    UsuarioQueryQuery,
    UsuarioQueryQueryVariables
  >({
    queryKey: ['usuario', id],
    queryFn: async () =>
      await request<UsuarioQueryQuery, UsuarioQueryQueryVariables>(
        USUARIO_QUERY,
        { variables: id ? { id } : undefined }
      ),
    enabled: !!id
  })

  const { data: usuarioDominioData, isLoading: usuarioDominioLoading } =
    useQuery<UsuarioDominioQueryQuery, UsuarioDominioQueryQueryVariables>({
      queryKey: ['usuarioDominio'],
      queryFn: async () =>
        await request<UsuarioDominioQueryQuery>(USUARIO_DOMINIO_QUERY)
    })

  const { data: cargosData, isLoading: cargosLoading } = useQuery<
    CargosQuery,
    CargosQueryVariables
  >({
    queryKey: ['cargos', id],
    queryFn: async () =>
      await request<CargosQuery, CargosQueryVariables>(CARGOS_QUERY, {
        variables: { cargosPrincipais: [id!] }
      }),
    enabled: !!id
  })
  const { data: cargosByUsuarioData, isLoading: cargosByUsuarioLoading } =
    useQuery<CargosByUsuarioQuery, CargosByUsuarioQueryVariables>({
      queryKey: ['cargosByUsuario', id],
      queryFn: async () =>
        await request<CargosByUsuarioQuery, CargosByUsuarioQueryVariables>(
          CARGOS_BY_USUARIO_QUERY,
          {
            variables: { usuarioId: id! }
          }
        ),
      enabled: !!id
    })
  const { data: infoUsuarioData, isLoading: infoUsuarioLoading } = useQuery<
    InfoUsuarioQueryQuery,
    InfoUsuarioQueryQueryVariables
  >({
    queryKey: ['infoUsuario', id],
    queryFn: async () =>
      await request<InfoUsuarioQueryQuery, InfoUsuarioQueryQueryVariables>(
        INFO_USUARIO_QUERY,
        { variables: { usuarioId: id! } }
      ),
    enabled: !!id
  })
  const {
    data: usuariosPortalPropertiesData,
    isLoading: usuariosPortalPropertiesLoading
  } = useQuery<
    UsuariosPortalPropertiesQueryQuery,
    UsuariosPortalPropertiesQueryQueryVariables
  >({
    queryKey: ['usuariosPortalProperties'],
    queryFn: async () =>
      await request<
        UsuariosPortalPropertiesQueryQuery,
        UsuariosPortalPropertiesQueryQueryVariables
      >(USUARIOS_PORTAL_PROPERTIES_QUERY),
    staleTime: 0
  })

  const salvarUsuarioMutation = useMutation({
    mutationFn: async ({
      variables
    }: {
      variables: SalvarUsuarioMutationVariables
    }) =>
      await request<SalvarUsuarioMutation, SalvarUsuarioMutationVariables>(
        SALVAR_USUARIO,
        { variables }
      ),
    onSuccess: async () => {
      await client.refetchQueries({ queryKey: ['usuario'] })
      await client.refetchQueries({ queryKey: ['usuariosPaged'] })
    }
  })

  const salvarUsuario = useCallback(
    (usuario, cargos, infos, successMessage, onClose) => {
      askUser({
        onConfirm: async () => {
          await salvarUsuarioMutation.mutateAsync({
            variables: { usuario, cargos, infos }
          })
          onClose()
        },
        title: t`Deseja salvar o usuario ${usuario.nome}?`,
        confirmLabel: t`Salvar`,
        successMessage: `${successMessage}`
      })
    },
    [salvarUsuarioMutation, askUser]
  )

  const allDataLoaded =
    !usuarioDominioLoading &&
    !usuarioLoading &&
    !infoUsuarioLoading &&
    !cargosByUsuarioLoading &&
    !usuariosPortalPropertiesLoading &&
    !cargosLoading

  const { produtos, marcas, tiposUsuario, grupos, dealers } =
    usuarioDominioData ?? {}
  const cargos = cargosByUsuarioData?.cargos ?? []
  const { infoUsuario } = infoUsuarioData ?? {}
  const {
    tiposUsuarioDocumentoObrigatorio = [],
    tiposUsuarioInformarCargo = [],
    tiposUsuarioInformarGestao = []
  } = usuariosPortalPropertiesData?.usuariosPortalProperties ?? {}

  const initialValuesMemoized = useMemo(() => {
    return {
      produtos,
      marcas,
      tiposUsuario,
      grupos,
      dealers,
      cargos,
      infoUsuario
    }
  }, [usuarioDominioData, cargosByUsuarioData, infoUsuarioData])

  const tiposMap = useMemo(() => {
    const dealerGrupoMap = {}
    const tiposUsuarioMap = []
    const tipoAcessoMap = {}
    dealers?.forEach(({ id, grupo: { id: grupoId } }) => {
      dealerGrupoMap[id] = grupoId
    })

    tiposUsuario?.forEach(tipo => {
      tiposUsuarioMap.push({
        ...tipo,
        description: `${tipo?.description}`
      })
    })

    for (let i = 0; i < produtos?.length; i++) {
      const produto = produtos?.[i]

      for (const tipoAcesso of produto.tiposAcesso) {
        tipoAcessoMap[tipoAcesso.id] = tipoAcesso
      }
    }
    return {
      dealerGrupoMap,
      tipoAcessoMap,
      tiposUsuarioMap
    }
  }, [dealers, tiposUsuario, produtos])

  return {
    usuarioData,
    usuarioDominioData,
    cargosData,
    cargosByUsuarioData,
    infoUsuarioData,
    usuariosPortalPropertiesData,
    salvarUsuario,
    allDataLoaded,
    produtos,
    marcas,
    tiposUsuario,
    cargos,
    infoUsuario,
    tiposUsuarioDocumentoObrigatorio,
    tiposUsuarioInformarCargo,
    tiposUsuarioInformarGestao,
    grupos,
    dealers,
    tiposMap,
    id,
    initialValuesMemoized
  }
}
